import { useMemo, useState } from 'react'
import { router } from '@inertiajs/react'
import { LoaderCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { TokenUsageCopy, TokenUsageReport } from '@/frontend/pages/Dashboard/DigitalWorkers/types'
import { buildDateRangePresets } from './dateRangePresets'

interface Props {
  workerId: string
  report: TokenUsageReport
  copy: TokenUsageCopy
}

export default function DateRangeFilter({ workerId, report, copy }: Props) {
  const [isApplying, setIsApplying] = useState(false)
  const presets = useMemo(
    () => buildDateRangePresets(report.max_date, copy.presets),
    [report.max_date, copy.presets],
  )

  const applyPreset = (dateFrom: string, dateTo: string) => {
    router.get(
      `/dashboard/digital_workers/${workerId}/uso`,
      { date_from: dateFrom, date_to: dateTo },
      {
        only: ['usage_report'],
        preserveScroll: true,
        preserveState: true,
        replace: true,
        onStart: () => setIsApplying(true),
        onFinish: () => setIsApplying(false),
      },
    )
  }

  return (
    <div role="group" aria-label={copy.period} className="inline-flex rounded-lg border border-border bg-secondary/45 p-0.5">
      {presets.map((preset) => {
        const isActive = preset.dateFrom === report.date_from && preset.dateTo === report.date_to

        return (
          <Button
            key={preset.key}
            type="button"
            variant="ghost"
            size="sm"
            disabled={isApplying}
            aria-pressed={isActive}
            onClick={() => applyPreset(preset.dateFrom, preset.dateTo)}
            className={`h-7 px-2.5 text-[11px] active:scale-[0.98] ${
              isActive
                ? 'bg-card font-medium text-foreground shadow-xs hover:bg-card'
                : 'text-muted-foreground hover:bg-card/65 hover:text-foreground'
            }`}
          >
            {isApplying && isActive && <LoaderCircle className="size-3 animate-spin motion-reduce:animate-none" />}
            {preset.label}
          </Button>
        )
      })}
    </div>
  )
}
